
import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

interface AddBridgeItemDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (name: string, value: number, description: string) => Promise<void>;
}

const AddBridgeItemDialog: React.FC<AddBridgeItemDialogProps> = ({
  open,
  onClose,
  onAdd
}) => {
  const [name, setName] = useState("");
  const [value, setValue] = useState("$");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove currency symbol and non-numeric characters except for the negative sign at the beginning
    const rawValue = e.target.value.replace(/^\$/, '');
    const numericValue = rawValue.replace(/[^0-9-]/g, '');
    
    // Only allow negative sign at the beginning
    const sanitizedValue = numericValue.replace(/(?!^)-/g, '');
    
    // Parse to number to remove leading zeros
    const numberValue = sanitizedValue ? parseInt(sanitizedValue, 10) : '';
    
    // Format with currency symbol and commas if it's a valid number
    if (typeof numberValue === 'number' && !isNaN(numberValue)) {
      setValue(`$${numberValue.toLocaleString('en-US')}`);
    } else {
      setValue(sanitizedValue ? `$${sanitizedValue}` : '$');
    }
  };

  const handleAdd = async () => {
    if (!name.trim()) {
      toast.error("Bridge component name is required");
      return;
    }
    
    // Parse the value by removing the currency symbol and commas
    const numValue = parseInt(value.replace(/^\$/, '').replace(/,/g, ''));
    if (isNaN(numValue)) {
      toast.error("Bridge component value must be a number");
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onAdd(name, numValue, description);
      handleClose();
      toast.success("Bridge component added successfully");
    } catch (error) {
      console.error("Error adding bridge component:", error);
      toast.error("Failed to add bridge component");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setName("");
    setValue("$");
    setDescription("");
    onClose();
  };

  return <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-white border-primary/20 text-black bg-white">
        <DialogTitle className="text-black">Add Bridge Component</DialogTitle>
        
        <div className="space-y-4 py-3">
          <div className="space-y-2">
            <Label htmlFor="bridge-name">Bridge component name:</Label>
            <Input id="bridge-name" value={name} onChange={e => setName(e.target.value)} className="text-black text-[13px] bg-white" />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="bridge-value">Bridge component value:</Label>
            <Input 
              id="bridge-value" 
              value={value} 
              onChange={handleValueChange} 
              className="text-black text-[13px] bg-white" 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="bridge-description">Bridge component description:</Label>
            <Input id="bridge-description" value={description} onChange={e => setDescription(e.target.value)} className="text-black text-[13px] bg-white" />
          </div>
        </div>
        
        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} className="bg-white rounded text-black mx-[12px] px-[25px]">
            Cancel
          </Button>
          <Button type="button" onClick={handleAdd} disabled={isSubmitting} className="text-black bg-primary font-normal rounded mx-px px-[36px]">
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
};

export default AddBridgeItemDialog;
